var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import deepmerge from "deepmerge";
import Mustache from "mustache";
import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getNavigatorLanguages } from "./getNavigatorLanguages";
import { useStorage } from "./storage";
import { defined } from "./utils";
/**
 * The key in the browser's storage, where the user-selected language is stored.
 */
const LANGUAGE_STORAGE_KEY = "variocube-language-v2";
/**
 * Creates a typed localization context.
 *
 * The language context is created within this function in order to enable correct typings on
 * the message object and the accessor functions.
 * @param options The options
 */
export function createLocalizationContext(options) {
    const { load, fallback, missing = defaultMissing } = options;
    /**
     * Returns a promise to the messages for the first language of the given list of languages
     * that could be successfully loaded.
     * @param languages The languages to try loading
     */
    const loadBestMatch = (languages) => __awaiter(this, void 0, void 0, function* () {
        for (const language of processLanguageList(languages)) {
            try {
                const messages = yield load(language);
                if (messages) {
                    document.documentElement.setAttribute("lang", language);
                    return { messages, language };
                }
            }
            catch (error) {
                // try next language
            }
        }
        throw new Error("Could not load any language. This is likely a configuration error.");
    });
    /**
     * The React context.
     */
    const LocalizationContext = createContext({
        messages: undefined,
        language: fallback,
        setLanguage: () => void 0,
        fallbackMessages: undefined,
    });
    /**
     * Localization provider component.
     */
    const LocalizationProvider = (props) => {
        const { children, language, onChangeLanguage, overrides } = props;
        const [loaded, setLoaded] = useState();
        // The context value
        const [value, setValue] = useState({
            messages: undefined,
            language: fallback,
            setLanguage: onChangeLanguage,
            fallbackMessages: undefined,
        });
        // Loads the fallback messages if required by the missing option
        useEffect(() => {
            if (missing == "fallback") {
                load(fallback)
                    .then(fallbackMessages => setValue(v => (Object.assign(Object.assign({}, v), { fallbackMessages }))))
                    .catch(error => console.error(error));
            }
        }, []);
        // Loads the best matching language and stores the result in `loaded`.
        useEffect(() => {
            loadBestMatch([language, ...getNavigatorLanguages(), fallback])
                .then(setLoaded)
                .catch(error => console.error(error));
        }, [language]);
        // Update context value when `loaded` or `overrides` change.
        useEffect(() => {
            if (loaded) {
                const { language, messages } = loaded;
                setValue(v => {
                    var _a;
                    return (Object.assign(Object.assign({}, v), { language, messages: deepmerge(messages, (_a = (overrides && overrides[language])) !== null && _a !== void 0 ? _a : {}) }));
                });
            }
        }, [overrides, loaded]);
        return (React.createElement(LocalizationContext.Provider, { value: value }, children));
    };
    /**
     * Localization provider that stores the current language in the local storage.
     */
    const StorageLocalizationProvider = ({ children, overrides }) => {
        // The user defined language, or null if user did not manually select a language
        const [userDefinedLanguage, setUserDefinedLanguage] = useStorage(LANGUAGE_STORAGE_KEY, null);
        return (React.createElement(LocalizationProvider, { language: userDefinedLanguage, onChangeLanguage: setUserDefinedLanguage, overrides: overrides }, children));
    };
    /**
     * Returns accessors to the currently used language.
     */
    const useLocalization = () => {
        const value = useContext(LocalizationContext);
        if (!value) {
            throw new Error("Cannot find localization context. Are you missing a LocalizationProvider in your component tree?");
        }
        const { messages, language, setLanguage, fallbackMessages } = value;
        const handleMissing = useCallback((key) => {
            if (missing == "fallback") {
                return fallbackMessages ? getString(fallbackMessages, key) : undefined;
            }
            return missing(key);
        }, [fallbackMessages]);
        const t = useCallback((key, context) => {
            var _a;
            if (messages) {
                const localized = (_a = getString(messages, key)) !== null && _a !== void 0 ? _a : handleMissing(key);
                if (localized) {
                    return Mustache.render(localized, context);
                }
                return localized;
            }
        }, [messages, handleMissing]);
        function _hasKey(key) {
            return defined(messages) && defined(getString(messages, key));
        }
        const hasKey = useCallback(_hasKey, [messages]);
        const e = useCallback((key, name) => t(key + "." + name), [t]);
        function _s(prefix) {
            return (key) => t(`${prefix}.${key}`);
        }
        const s = useCallback(_s, [t]);
        return {
            t,
            hasKey,
            e,
            s,
            language,
            setLanguage,
        };
    };
    return {
        LocalizationProvider,
        StorageLocalizationProvider,
        useLocalization,
    };
}
/**
 * Processes a list of languages:
 *  - Skips null or empty languages
 *  - Expands a language by adding additional entries for the language component of locale notations.
 * Example: an entry "en-US" turns into two entries "en-US" and "en".
 * @param languages
 */
function processLanguageList(languages) {
    const expanded = [];
    for (const language of languages) {
        if (language) {
            // push the original language, e.g. "en-US"
            expanded.push(language);
            // push the language only, e.g.
            const languageOnly = language.split("-").shift();
            if (languageOnly && languageOnly != language) {
                expanded.push(languageOnly);
            }
        }
    }
    // avoid duplicates
    return new Set(expanded);
}
/**
 * Default handler for missing keys.
 * @param key The missing key.
 */
function defaultMissing(key) {
    console.warn("Missing translation key", key);
    return "";
}
function getString(obj, key) {
    var _a;
    return (_a = getKey(obj, key)) === null || _a === void 0 ? void 0 : _a.toString();
}
function getKey(obj, key) {
    return key.split(".")
        .reduce((obj, key) => obj && obj[key], obj);
}
/**
 * Creates a simple TFunc from an object.
 */
export function createSimpleTFunc(obj) {
    return (key) => { var _a; return (_a = getString(obj, key)) !== null && _a !== void 0 ? _a : "key not found"; };
}
//# sourceMappingURL=localization.js.map